<div class="flex flex-col gap-4 p-4">
  <!-- ── Header + Mode Toggle ─────────────────────────────────────────────── -->
  <div class="flex flex-wrap items-center justify-between gap-3">
    <div>
      <h1 class="text-xl font-bold text-gray-900">Staff Performance</h1>
      <p class="text-sm text-gray-500">Pending workload and activity by user and module</p>
    </div>
    <div class="flex items-center rounded-full border border-gray-200 bg-gray-50 p-0.5">
      <button
        class="rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
        [ngClass]="mode === 'user' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'"
        (click)="setMode('user')"
      >
        By User
      </button>
      <button
        class="rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
        [ngClass]="mode === 'module' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'"
        (click)="setMode('module')"
      >
        By Module
      </button>
    </div>
  </div>

  @if (mode === 'user' && cameFromModule) {
    <div class="-my-2 flex justify-end">
      <button type="button" class="flex w-fit items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-primary" (click)="backToModule()">
        <mat-icon class="!h-4 !w-4 !text-base !leading-none">arrow_back</mat-icon>
        Back to {{ getModuleLabel(cameFromModule) }}
      </button>
    </div>
  }

  <!-- ── Filter Bar ────────────────────────────────────────────────────────── -->
  <div class="rounded-xl border border-gray-100 bg-white px-4 py-1 shadow-sm">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- ── Two-panel layout ──────────────────────────────────────────────────── -->
  <div class="flex flex-col gap-4 md:flex-row" style="min-height: 0">
    <!-- MAIN DETAIL AREA — natural flow on mobile; constrained scroll on md+.
         Uses space-y-4 rather than flex-col so children keep their natural height
         (flex would otherwise squeeze them when total content exceeds max-height). -->
    <div class="min-w-0 flex-1 overflow-x-hidden md:max-h-[calc(100vh-220px)] md:overflow-y-auto">
      <div class="grid gap-4">
        <!-- Mobile: horizontally-scrollable chip list (replaces hidden sidebar) -->
        <div class="w-full overflow-hidden md:hidden">
          @if (mode === 'user' && rosterData.users.length > 0) {
            <div class="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              @for (u of rosterData.users; track u.user_id) {
                <button
                  class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                  [ngClass]="selectedUserId === u.user_id ? 'border-primary bg-primary-50 text-primary' : 'border-gray-200 bg-white text-gray-700'"
                  (click)="selectUser(u.user_id)"
                >
                  <img class="h-5 w-5 shrink-0 rounded-full object-cover" [src]="u.profile_picture | avatar: u.name" [alt]="u.name" />
                  <span>{{ u.name }}</span>
                  <span class="rounded-full px-1.5 py-0 text-[10px] font-bold" [ngClass]="getPendingBadgeClass(u.total_pending)">{{ u.total_pending }}</span>
                </button>
              }
            </div>
          }
          @if (mode === 'module') {
            <div class="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              @for (m of STAFF_MODULES; track m.key) {
                <button
                  class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                  [ngClass]="selectedModule === m.key ? 'border-primary bg-primary-50 text-primary' : 'border-gray-200 bg-white text-gray-700'"
                  (click)="selectModule(m.key)"
                >
                  <mat-icon class="!h-3.5 !w-3.5 !text-xs !leading-none">{{ m.icon }}</mat-icon>
                  <span>{{ m.label }}</span>
                  <span class="rounded-full px-1.5 py-0 text-[10px] font-bold" [ngClass]="getPendingBadgeClass(getModuleTotal(m.key))">{{ getModuleTotal(m.key) }}</span>
                </button>
              }
            </div>
          }
        </div>

        <!-- Overview charts (landing panel — shown when nothing is selected) -->
        @if (!selectedUserId && !selectedModule) {
          @if (loadingCharts) {
            <div class="flex min-h-[300px] items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm">
              <mat-spinner diameter="32"></mat-spinner>
            </div>
          } @else {
            <!-- USER MODE → line chart: active / blocked / archived per month -->
            @if (mode === 'user' && userTrendChartOpts) {
              <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div class="mb-1 flex items-center gap-2">
                  <mat-icon class="!text-base text-gray-400">people</mat-icon>
                  <h3 class="text-sm font-bold text-gray-800">{{ trendChartTitle }}</h3>
                </div>
                <p class="mb-3 text-xs text-gray-400">Cumulative user headcount per month, grouped by current status</p>
                <apx-chart
                  [series]="userTrendChartOpts.series"
                  [chart]="userTrendChartOpts.chart"
                  [stroke]="userTrendChartOpts.stroke"
                  [colors]="userTrendChartOpts.colors"
                  [xaxis]="userTrendChartOpts.xaxis"
                  [yaxis]="userTrendChartOpts.yaxis"
                  [legend]="userTrendChartOpts.legend"
                  [tooltip]="userTrendChartOpts.tooltip"
                  [grid]="userTrendChartOpts.grid"
                  [markers]="userTrendChartOpts.markers"
                />
              </div>
            }

            <!-- MODULE MODE → bar chart: pending count per module -->
            @if (mode === 'module' && modulePendingChartOpts) {
              <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div class="mb-1 flex items-center gap-2">
                  <mat-icon class="!text-base text-gray-400">bar_chart</mat-icon>
                  <h3 class="text-sm font-bold text-gray-800">Pending Items by Module</h3>
                </div>
                <p class="mb-3 text-xs text-gray-400">Current pending workload across all staff, company-wide</p>
                <apx-chart
                  [series]="modulePendingChartOpts.series"
                  [chart]="modulePendingChartOpts.chart"
                  [plotOptions]="modulePendingChartOpts.plotOptions"
                  [colors]="modulePendingChartOpts.colors"
                  [legend]="modulePendingChartOpts.legend"
                  [dataLabels]="modulePendingChartOpts.dataLabels"
                  [xaxis]="modulePendingChartOpts.xaxis"
                  [yaxis]="modulePendingChartOpts.yaxis"
                  [tooltip]="modulePendingChartOpts.tooltip"
                  [grid]="modulePendingChartOpts.grid"
                />
              </div>
            }
          }
        }

        <!-- Loading -->
        @if ((selectedUserId || selectedModule) && loadingDetail) {
          <div class="flex min-h-[300px] items-center justify-center rounded-xl border border-gray-100 bg-white p-12 shadow-sm">
            <mat-spinner diameter="32"></mat-spinner>
          </div>
        }

        <!-- Error -->
        @if (detailError) {
          <div class="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-600">{{ detailError }}</div>
        }

        <!-- ──────────────────────────────────────────────────────────────────── -->
        <!-- BY USER DETAIL                                                       -->
        <!-- ──────────────────────────────────────────────────────────────────── -->
        @if (mode === 'user' && selectedUserId && userDetail && !loadingDetail) {
          <!-- Identity strip -->
          <div class="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <img class="h-12 w-12 shrink-0 rounded-full object-cover" [src]="userDetail.profile_picture | avatar: userDetail.name" [alt]="userDetail.name" />
            <div class="min-w-0 flex-1">
              <h2 class="truncate text-lg font-bold text-gray-900">{{ userDetail.name }}</h2>
              <span class="text-sm text-gray-500">{{ userDetail.role || 'Staff Member' }}</span>
            </div>
          </div>

          <!-- 4 KPI tiles — 1-col on phones, 2-col on sm, 4-col on lg -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <!-- Total Pending — snapshot, no comparison -->
            <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div class="text-xs font-bold uppercase tracking-wide text-gray-400">Total Pending</div>
              <div class="mt-1 text-2xl font-bold" [ngClass]="(userDetail.kpis?.total_pending ?? 0) > 20 ? 'text-red-600' : 'text-gray-800'">
                {{ userDetail.kpis?.total_pending ?? 0 }}
              </div>
            </div>

            <!-- Completed (Period) — compare supported -->
            <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div class="text-xs font-bold uppercase tracking-wide text-gray-400">Completed (Period)</div>
              <div class="mt-1 text-2xl font-bold text-green-600">{{ userDetail.kpis?.completed_mtd ?? 0 }}</div>
              @if (hasCompare && userDetail.kpis?.compare_completed_mtd !== null && userDetail.kpis?.compare_completed_mtd !== undefined) {
                <div class="mt-2 flex items-center gap-1.5">
                  <span
                    class="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold"
                    [ngClass]="getDeltaBadgeClass(userDetail.kpis.completed_mtd, userDetail.kpis.compare_completed_mtd, true)"
                  >
                    <mat-icon class="!h-3 !w-3 !text-[11px] !leading-none">{{ getDeltaIcon(userDetail.kpis.completed_mtd, userDetail.kpis.compare_completed_mtd) }}</mat-icon>
                    {{ getDeltaPct(userDetail.kpis.completed_mtd, userDetail.kpis.compare_completed_mtd) }}%
                  </span>
                  <span class="text-xs text-gray-400">vs prev</span>
                </div>
              }
            </div>

            <!-- Oldest Pending — snapshot, no comparison -->
            <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div class="text-xs font-bold uppercase tracking-wide text-gray-400">Oldest Pending (days)</div>
              <div class="mt-1 text-2xl font-bold" [ngClass]="getAgingClass(userDetail.kpis?.oldest_pending_days ?? 0)">{{ userDetail.kpis?.oldest_pending_days ?? 0 }}</div>
            </div>

            <!-- Active Days — compare supported -->
            <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div class="text-xs font-bold uppercase tracking-wide text-gray-400">Active Days (Period)</div>
              <div class="mt-1 text-2xl font-bold text-gray-800">{{ userDetail.kpis?.active_days_mtd ?? 0 }}</div>
              @if (hasCompare && userDetail.kpis?.compare_active_days_mtd !== null && userDetail.kpis?.compare_active_days_mtd !== undefined) {
                <div class="mt-2 flex items-center gap-1.5">
                  <span
                    class="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold"
                    [ngClass]="getDeltaBadgeClass(userDetail.kpis.active_days_mtd, userDetail.kpis.compare_active_days_mtd, true)"
                  >
                    <mat-icon class="!h-3 !w-3 !text-[11px] !leading-none">{{ getDeltaIcon(userDetail.kpis.active_days_mtd, userDetail.kpis.compare_active_days_mtd) }}</mat-icon>
                    {{ getDeltaPct(userDetail.kpis.active_days_mtd, userDetail.kpis.compare_active_days_mtd) }}%
                  </span>
                  <span class="text-xs text-gray-400">vs prev</span>
                </div>
              }
            </div>
          </div>

          <!-- Module cards — 2-column grid on large screens -->
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            @for (mod of userDetail.modules; track mod.key) {
              @if (isModuleVisible(mod.key) && ((mod.pending_total ?? 0) > 0 || (mod.done_mtd ?? 0) > 0)) {
                <div class="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                  <!-- Card header with color left-border accent -->
                  <div class="flex items-center gap-3 border-b border-l-4 px-4 py-3" [style.border-left-color]="getModuleColor(mod.key)">
                    <mat-icon class="!h-5 !w-5 !text-[18px] !leading-none" [style.color]="getModuleColor(mod.key)">
                      {{ getModuleIcon(mod.key) }}
                    </mat-icon>
                    <span class="flex-1 text-sm font-bold text-gray-800">{{ mod.label }}</span>
                    <span class="text-xl font-bold" [style.color]="getModuleColor(mod.key)">{{ mod.pending_total ?? 0 }}</span>
                    <span class="text-xs text-gray-400">pending</span>
                  </div>

                  <!-- 3 mini KPIs -->
                  <div class="grid grid-cols-3 divide-x border-b">
                    <div class="px-3 py-2 text-center">
                      <div class="text-xs uppercase tracking-wide text-gray-400">Done</div>
                      <div class="text-sm font-bold text-green-600">{{ mod.done_mtd ?? 0 }}</div>
                      @if (hasCompare && mod.compare_done_mtd !== null && mod.compare_done_mtd !== undefined) {
                        <div
                          class="mt-0.5 inline-flex items-center gap-0.5 rounded-full px-1 py-0.5 text-[10px] font-semibold"
                          [ngClass]="getDeltaBadgeClass(mod.done_mtd, mod.compare_done_mtd, true)"
                        >
                          <mat-icon class="!h-2.5 !w-2.5 !text-[10px] !leading-none">{{ getDeltaIcon(mod.done_mtd, mod.compare_done_mtd) }}</mat-icon>
                          {{ getDeltaPct(mod.done_mtd, mod.compare_done_mtd) }}%
                        </div>
                      }
                    </div>
                    <div class="px-3 py-2 text-center">
                      <div class="text-xs uppercase tracking-wide text-gray-400">Oldest</div>
                      <div class="text-sm font-bold" [ngClass]="getAgingClass(mod.oldest_days ?? 0)">{{ mod.oldest_days ?? 0 }} d</div>
                    </div>
                    <div class="px-3 py-2 text-center">
                      <div class="text-xs uppercase tracking-wide text-gray-400">Pending</div>
                      <div class="text-sm font-bold text-gray-700">{{ mod.pending_total ?? 0 }}</div>
                    </div>
                  </div>

                  <!-- Aging bar -->
                  <div class="border-b px-4 py-2">
                    <div class="flex h-2 overflow-hidden rounded bg-gray-100">
                      <div class="bg-green-400 transition-all" [style.width.%]="getAgingPct(mod.aging, 'b0')"></div>
                      <div class="bg-yellow-400 transition-all" [style.width.%]="getAgingPct(mod.aging, 'b1')"></div>
                      <div class="bg-orange-400 transition-all" [style.width.%]="getAgingPct(mod.aging, 'b2')"></div>
                      <div class="bg-red-500 transition-all" [style.width.%]="getAgingPct(mod.aging, 'b3')"></div>
                      <div class="bg-red-800 transition-all" [style.width.%]="getAgingPct(mod.aging, 'b4')"></div>
                    </div>
                    <div class="mt-1 flex justify-between text-xs text-gray-400">
                      <span>0–3d · {{ mod.aging?.b0 ?? 0 }}</span>
                      <span>4–7d · {{ mod.aging?.b1 ?? 0 }}</span>
                      <span>8–14d · {{ mod.aging?.b2 ?? 0 }}</span>
                      <span>15–30d · {{ mod.aging?.b3 ?? 0 }}</span>
                      <span>30+d · {{ mod.aging?.b4 ?? 0 }}</span>
                    </div>
                  </div>

                  <!-- Sub-item rows -->
                  @if (mod.sub_items?.length > 0) {
                    @for (item of mod.sub_items; track item.status) {
                      <div class="flex items-center gap-3 border-b border-gray-50 px-4 py-2 last:border-0">
                        <span class="flex-1 text-xs text-gray-600">{{ item.label }}</span>
                        <span class="w-8 text-right text-sm font-bold text-gray-800">{{ item.count }}</span>
                        <span class="w-10 text-right text-xs" [ngClass]="getAgingClass(item.oldest_days ?? 0)"> {{ item.oldest_days ?? 0 }} d</span>
                      </div>
                    }
                  } @else {
                    <div class="px-4 py-3 text-xs text-gray-400">No pending items</div>
                  }
                </div>
              }
            }
          </div>
        }

        <!-- ──────────────────────────────────────────────────────────────────── -->
        <!-- BY MODULE DETAIL                                                     -->
        <!-- ──────────────────────────────────────────────────────────────────── -->
        @if (mode === 'module' && selectedModule && moduleDetail && !loadingDetail) {
          <!-- Module summary strip -->
          <div class="flex flex-wrap items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <mat-icon class="!h-10 !w-10 !text-4xl !leading-none" [style.color]="getModuleColor(selectedModule)">
              {{ getModuleIcon(selectedModule) }}
            </mat-icon>
            <div class="flex-1">
              <h2 class="text-lg font-bold text-gray-900">{{ moduleDetail.label }}</h2>
              <div class="text-sm text-gray-500">
                <span class="font-semibold text-gray-800">{{ moduleDetail.summary?.total_pending ?? 0 }}</span>
                <span [matTooltip]="getPendingStatusTooltip(selectedModule)"> pending</span>
                @if ((moduleDetail.summary?.assignees ?? 0) > 0) {
                  · <span class="font-semibold text-gray-800">{{ moduleDetail.summary.assignees }}</span> assignees
                }
                · oldest <span [ngClass]="getAgingClass(moduleDetail.summary?.oldest_days ?? 0)">{{ moduleDetail.summary?.oldest_days ?? 0 }} d</span>
              </div>
            </div>
            <!-- Done in period with compare badge -->
            <div class="text-center">
              <div class="text-xs font-bold uppercase tracking-wide text-gray-400" [matTooltip]="getDoneStatusTooltip(selectedModule)">Done (Period)</div>
              <div class="text-xl font-bold text-green-600">{{ moduleDetail.summary?.done_current ?? 0 }}</div>
              @if (hasCompare && moduleDetail.summary?.done_compare !== null && moduleDetail.summary?.done_compare !== undefined) {
                <div class="mt-1 flex items-center justify-center gap-1">
                  <span
                    class="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold"
                    [ngClass]="getDeltaBadgeClass(moduleDetail.summary.done_current, moduleDetail.summary.done_compare, true)"
                  >
                    <mat-icon class="!h-3 !w-3 !text-[11px] !leading-none">{{ getDeltaIcon(moduleDetail.summary.done_current, moduleDetail.summary.done_compare) }}</mat-icon>
                    {{ getDeltaPct(moduleDetail.summary.done_current, moduleDetail.summary.done_compare) }}%
                  </span>
                  <span class="text-xs text-gray-400">vs prev</span>
                </div>
              }
            </div>
          </div>

          <!-- Sub-types table -->
          @if (moduleDetail.sub_types?.length > 0) {
            <div class="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div class="border-b px-4 py-3">
                <h3 class="text-sm font-bold text-gray-800">Pending by status</h3>
              </div>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-gray-50">
                    <tr class="text-left text-xs font-bold uppercase tracking-wide text-gray-400">
                      <th class="px-4 py-2">Status</th>
                      <th class="px-4 py-2 text-right">Count</th>
                      <th class="px-4 py-2 text-right">Oldest</th>
                      <th class="px-4 py-2 text-right">Avg age</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-50">
                    @for (s of moduleDetail.sub_types; track s.status) {
                      <tr>
                        <td class="px-4 py-2.5 text-gray-700">{{ s.label }}</td>
                        <td class="px-4 py-2.5 text-right font-bold text-gray-800">{{ s.total }}</td>
                        <td class="px-4 py-2.5 text-right" [ngClass]="getAgingClass(s.oldest_days ?? 0)">{{ s.oldest_days ?? 0 }} d</td>
                        <td class="px-4 py-2.5 text-right text-gray-500">{{ s.avg_days ?? 0 }} d</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          }

          <!-- User leaderboard -->
          @if (moduleDetail.users?.length > 0) {
            <div class="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div class="border-b px-4 py-3">
                <h3 class="text-sm font-bold text-gray-800">
                  Pending by user
                  <span class="ml-2 text-xs font-normal text-gray-400">{{ moduleDetail.users.length }} assignees</span>
                </h3>
              </div>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-gray-50">
                    <tr class="text-left text-xs font-bold uppercase tracking-wide text-gray-400">
                      <th class="px-4 py-2">User</th>
                      @for (s of moduleDetail.sub_types; track s.status) {
                        <th class="px-4 py-2 text-right">{{ s.status }}</th>
                      }
                      <th class="px-4 py-2 text-right">Total</th>
                      <th class="px-4 py-2 text-right">Oldest</th>
                      <th class="px-4 py-2 text-right">Done</th>
                      @if (hasCompare) {
                        <th class="px-4 py-2 text-right">vs Prev</th>
                      }
                      <th class="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-50">
                    @for (u of moduleDetail.users; track u.user_id) {
                      <tr [class.cursor-pointer]="!u.is_external" [class.hover:bg-gray-50]="!u.is_external" (click)="!u.is_external && selectUser(u.linked_user_id, selectedModule)">
                        <td class="px-4 py-2.5 font-medium text-gray-800">{{ u.name }}</td>
                        @for (c of u.sub_counts; track $index) {
                          <td class="px-4 py-2.5 text-right text-gray-600">{{ c }}</td>
                        }
                        <td class="px-4 py-2.5 text-right font-bold text-gray-800">{{ u.total }}</td>
                        <td class="px-4 py-2.5 text-right" [ngClass]="getAgingClass(u.oldest_days ?? 0)">{{ u.oldest_days ?? 0 }} d</td>
                        <td class="px-4 py-2.5 text-right font-semibold text-green-700">{{ u.done_current ?? 0 }}</td>
                        @if (hasCompare) {
                          <td class="px-4 py-2.5 text-right">
                            @if (u.done_compare !== null && u.done_compare !== undefined) {
                              <span class="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold" [ngClass]="getDeltaBadgeClass(u.done_current, u.done_compare, true)">
                                <mat-icon class="!h-3 !w-3 !text-[11px] !leading-none">{{ getDeltaIcon(u.done_current, u.done_compare) }}</mat-icon>
                                {{ getDeltaPct(u.done_current, u.done_compare) }}%
                              </span>
                            }
                          </td>
                        }
                        <td class="px-4 py-2.5 text-right">
                          <mat-icon class="!h-4 !w-4 !text-base !leading-none text-primary">arrow_forward</mat-icon>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          }

          <!-- Accounting: no user leaderboard -->
          @if (selectedModule === 'accounting' && moduleDetail.users?.length === 0) {
            <div class="rounded-xl border border-blue-50 bg-blue-50 p-4 text-sm text-blue-700 shadow-sm">
              <mat-icon class="!text-base !leading-none">info</mat-icon>
              Accounting bills are not user-assigned. Review the status breakdown above to track workload.
            </div>
          }
        }
      </div>
    </div>

    <!-- LEFT SIDEBAR — desktop only (md+); mobile uses inline pill list below -->
    <div class="hidden w-full shrink-0 flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm md:flex md:max-h-[calc(100vh-220px)] md:w-72">
      <!-- Sidebar header -->
      <div class="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <span class="text-xs font-bold uppercase tracking-wide text-gray-500">
          @if (mode === 'user') {
            Users · {{ rosterData.users.length }}
          } @else {
            Modules · {{ STAFF_MODULES.length }}
          }
        </span>
        @if (mode === 'user') {
          <span class="text-xs text-gray-400">By pending ↓</span>
        }
      </div>

      <!-- Sidebar body -->
      <div class="flex-1 overflow-y-auto">
        <!-- USER LIST -->
        @if (mode === 'user') {
          @if (loadingRoster) {
            <div class="flex items-center justify-center py-10">
              <mat-spinner diameter="24"></mat-spinner>
            </div>
          } @else if (rosterData.users.length === 0) {
            <div class="px-4 py-10 text-center text-sm text-gray-400">No active staff found</div>
          } @else {
            @for (u of rosterData.users; track u.user_id) {
              <div
                class="flex cursor-pointer items-center gap-3 border-b border-l-2 border-gray-50 px-4 py-2.5 transition-colors"
                [ngClass]="selectedUserId === u.user_id ? 'border-l-primary bg-primary-50' : 'border-l-transparent hover:bg-gray-50'"
                (click)="selectUser(u.user_id)"
              >
                <!-- Profile picture (falls back to UI-avatars initials when no photo) -->
                <img class="h-8 w-8 shrink-0 rounded-full object-cover" [src]="u.profile_picture | avatar: u.name" [alt]="u.name" />
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-semibold text-gray-800">{{ u.name }}</div>
                  <div class="truncate text-xs text-gray-400">{{ u.role || 'Staff' }}</div>
                </div>
                <span class="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold" [ngClass]="getPendingBadgeClass(u.total_pending)">
                  {{ u.total_pending }}
                </span>
              </div>
            }
          }
        }

        <!-- MODULE LIST -->
        @if (mode === 'module') {
          @for (m of STAFF_MODULES; track m.key) {
            <div
              class="flex cursor-pointer items-center gap-3 border-b border-l-2 border-gray-50 px-4 py-2.5 transition-colors"
              [ngClass]="selectedModule === m.key ? 'border-l-primary bg-primary-50' : 'border-l-transparent hover:bg-gray-50'"
              (click)="selectModule(m.key)"
            >
              <mat-icon class="!h-5 !w-5 !text-[18px] !leading-none text-gray-400">{{ m.icon }}</mat-icon>
              <span class="flex-1 text-sm font-semibold text-gray-800">{{ m.label }}</span>
              <span class="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold" [ngClass]="getPendingBadgeClass(getModuleTotal(m.key))">
                {{ getModuleTotal(m.key) }}
              </span>
            </div>
          }
        }
      </div>
    </div>
  </div>
</div>
